/*
 * PLT-6269 — the frame of the supplier results panel.
 *
 * CSS Modules rather than the styled-components the rest of this dashboard is written in:
 * the repo's rule is that new component files use CSS Modules, `suppliers/` has no
 * styled-components of its own to be consistent with, and the dashboard already carries
 * `.module.css` files (`DocumentationSection`, `DoubleMaterialityOverview`).
 *
 * Every colour here is a token, and no rule in this file sets a font size or a line height —
 * every word on the surface comes from `Text` or `Heading`.
 *
 * **The one set of literals is the level mark's own geometry** (`.meter`, `.segment`): its 8px
 * squares, 3px gaps and 2px corners are the size, internal spacing and shape *of a mark*, not
 * space between elements, so they no more belong on the space or radius scale than an icon's
 * 16px does. The spacing *around* the mark is tokenised like everything else.
 */

/*
 * A floating panel, positioned exactly like the agentic chat panel.
 *
 * It was a push panel until Roni asked for this (2026-09-15): one right-hand panel geometry for
 * the whole page, so a reader who has learned where the agent opens finds supplier results in the
 * same place at the same size. `.chat-side-panel` in `chat/upright-chat-theme.css` is the source
 * of every number below, and the two are meant to stay identical — a copy that drifts is worse
 * than no copy, because the two panels then land a few pixels apart on the same page.
 *
 * What it costs, stated because it is a real trade: the panel covers the right edge of the list
 * instead of making room beside it, so the two counts, the bin and the right of the Overall risk
 * column sit behind it while it is open. The chat panel already behaves this way, only one of the
 * two is ever open (D28), the panel's own footer carries Delete, and the band it covers is the one
 * it restates as its own headline. The alternative — reserving the panel's width beside the table —
 * squeezes the list below the 772px its columns need and clips the last one, which is the failure
 * that reads as broken rather than tight.
 */
.panel {
  /*
   * `fixed`, so the panel's height is the viewport's rather than the row's, and `top` with
   * `bottom` is what fixes it: the frame is the same height whatever the supplier holds, and
   * `.body` is the only part that moves (design/50-components.md — the frame holds still and the
   * middle scrolls).
   *
   * 48px is `HEADER_HEIGHT_PX` (`common/generic/layout/layout-constants.ts`), written as a
   * literal for the same reason `upright-chat-theme.css` writes it as one for the chat panel: a
   * CSS module cannot read a TS constant.
   */
  position: fixed;
  top: calc(48px + var(--space__md));
  right: var(--space__md);
  bottom: var(--space__md);
  width: clamp(360px, 42vw, 600px);
  display: flex;
  flex-direction: column;
  background: var(--color__white);
  /* `lg` is the dashboard's card radius. `overflow: hidden` is what keeps the body's own
     scroll inside the rounded corners. */
  border-radius: var(--border-radius__lg);
  /*
   * Elevation and no border, because the panel is above the page now rather than in it — the
   * chat panel's own choice, for the same reason. The two raw rgba layers are copied rather than
   * tokenised: `assets/styles/tokens.css` carries no shadow or elevation scale at all, so there
   * is nothing to reference, and minting the design system's first elevation token inside a
   * feature module is not this branch's call to make.
   */
  box-shadow:
    0 12px 32px rgba(31, 35, 40, 0.16),
    0 2px 6px rgba(31, 35, 40, 0.06);
  /*
   * The chat panel's layer: above page content and above the global header (1000). It is also
   * exactly what the design system's `Modal` overlay takes, and equal is right rather than lower
   * — the delete confirmation this panel's footer opens is portalled to the end of the body, so
   * at the same z-index it still paints over the panel, while the page's own chrome cannot.
   */
  z-index: 1100;
  overflow: hidden;
  animation: panel-slide-in 180ms ease-out;
}

/* The chat panel's entrance, so the two surfaces arrive the same way as well as in the same
   place. CSS Modules scopes the name, so it cannot collide with the chat theme's copy. */
@keyframes panel-slide-in {
  from {
    transform: translateX(16px);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel {
    animation: none;
  }
}

/*
 * `xl` is the card inset, which is what the three bands of this panel are: the header, the
 * body and the footer all take it, so the reader's eye finds one left edge down the whole
 * panel. `md` between the name row and the state row — a step in from the inset.
 */
.header {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
  padding: var(--space__xl);
  /*
   * A line, where a dialog's chrome would use space (design/50-components.md § Dialog).
   * The difference is the scroll: the body moves under this edge, so without the hairline
   * the first row that scrolls up reads as part of the header.
   */
  border-bottom: 1px solid var(--color__gray-200);
}

.headerTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space__sm);
}

/* Lets the name truncate instead of pushing the close button off the panel's edge. */
.headerName {
  min-width: 0;
}

/* `lg` between sections: one step in from the `xl` inset, and one step out from the `md` a
   section puts between its own label and its rows. The overall block and the summary block each add
   their own seam and their own space below it (`.overall`, `.summary`), so this gap is the air on the
   far side of each seam and nothing else. */
.body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space__xl);
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
}

.footer {
  flex: none;
  /* Right-aligned — the action row of a panel (design/50-components.md § Where the action row
     goes). Both of its controls are text rather than filled, so there is no primary/secondary
     ladder in this row; the panel's own footer comment carries why. */
  display: flex;
  justify-content: flex-end;
  /*
   * Wraps as insurance, not because it fires at any width the panel currently reaches. The binding
   * case is the panel's 360px floor (any window under about 860px): the two labelled controls and
   * their `sm` gap come to 255.31px against the 312.00px content box it leaves — one line, with
   * 56.69px to spare. At 1280x720 the panel is 538px, the box 489.60px and the slack 234.29px. Kept
   * anyway, because the headroom is a font-metric accident rather than a decision: a longer label, a
   * locale, or a different rendering of the same face eats it, and without the rule the buttons
   * shrink instead and their labels truncate mid-word with the icons clipped. Wrapping drops the
   * second control onto its own line, still right-aligned, with both labels whole.
   *
   * Most of that 56.69px is what round 3 bought. With the button still reading "Delete supplier" the
   * row measured 311.08px in the same 312.00px box — 0.92px of slack — which is why an earlier
   * version of this comment described the rule as all but load-bearing. Shortening the label to
   * "Delete" is what moved it.
   *
   * It was genuinely load-bearing until this panel took the chat panel's geometry (see `.panel`). At
   * the old `clamp(320px, 28vw, 400px)` the floor left a 272px content box, so the second button
   * wrapped on every window under about 1140px — which is why the rule is here and not on a later
   * branch.
   */
  flex-wrap: wrap;
  gap: var(--space__sm);
  padding: var(--space__xl);
  border-top: 1px solid var(--color__gray-200);
}

/*
 * The level mark: four squares, filled from the left.
 *
 * Ported from the MCP card (`src/ui/mcp-apps/supplier-risk/app.tsx`), which is the format
 * decision D25 asked this panel to match. A fixed 41px box — four 8px squares and three 3px
 * gaps — is what makes the column scannable: every row's mark starts and ends on the same two
 * lines, so the reader compares lengths and not positions.
 *
 * The height is explicit rather than left to the squares, so an empty `.meter` is still the
 * mark's own footprint. That is what the loading skeleton wraps.
 */
.meter {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 3px;
  width: 41px;
  height: 8px;
  overflow: hidden;
}

/*
 * Risk is the only axis this panel carries and purple is the hue the system reserves for
 * negative impact, so the squares take the valence layer's own two purples rather than the
 * card's raw hexes: `--valence__negative-strong` *is* the card's `#7F22FE`, and
 * `--valence__negative-border` replaces its untokenised `#E7E0FB` for the unfilled step.
 *
 * `-strong` at full strength is correct here where `design/50-components.md` warns against it
 * for text: that caveat is a 4.5:1 contrast rule about glyphs, and this is a filled shape.
 */
.segment {
  width: 8px;
  height: 8px;
  /*
   * 2px, the card's own corner, rather than `--border-radius__xs`: the radius scale bottoms out
   * at 3px, and 3px on an 8px box is 37% of its width — enough that the column reads as a row of
   * dots instead of squares. The square is the reading, so its corner is part of the mark's
   * geometry rather than a surface radius. Verified in Storybook, not in the diff.
   */
  border-radius: 2px;
  background-color: var(--valence__negative-border);
}

.segment[data-filled='true'] {
  background-color: var(--valence__negative-strong);
}

/*
 * The overall reading and its label, and the seam under them (Roni, 2026-09-15: "make visual
 * distinction between Overall supplier risk section and the individual categories").
 *
 * **A line here, where `design/50-components.md` § Dialog would use space**, for the reason that
 * section allows: a divider "still belongs *inside* the scroll area, separating blocks of content".
 * That is exactly this seam's job — the reading above it is the panel's one summary and everything
 * below it is the detail behind it, and `.body`'s flat `lg` gap said they were four peers.
 *
 * **`gray-300`, not the `gray-200` every other rule in this file draws.** The rows already carry
 * hairlines, so a seam at the rows' own weight would have read as one more row boundary; a step up
 * the gray ramp is what separates a *block* seam from a row seam, and 1px `gray-300` is the weight
 * `design/50-components.md` gives a divider. The only other one on the surface is `.summary`'s, and
 * both mark the same thing — the end of a block — so they are one rule rather than two to learn.
 *
 * `lg` below the line, and `.body`'s own `lg` gap below that, which puts 16px of air on each side of
 * the seam: the gap around a group beats the gaps inside it (design/10-foundations.md § Space and
 * rhythm), and inside this block the label and its chip sit `md` apart.
 */
.overall {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space__md);
  padding-bottom: var(--space__lg);
  border-bottom: 1px solid var(--color__gray-300);
}

.section {
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
}

/*
 * The executive summary and "For a buyer" as one block, with the seam under it (PLT-7497; Roni,
 * 2026-10-13: "a section separation line between 'For buyer' and ESRS topic risk levels").
 *
 * `.overall`'s seam, for `.overall`'s reason: the rows already draw `gray-200` hairlines, so a seam at
 * their weight would read as one more row boundary rather than the end of the narrative.
 *
 * **The seam belongs to this block, not to the list.** When the summary is unavailable the block is not
 * rendered, and the overall seam is then directly above the list; a line on the list's own top edge
 * would draw a second seam there with nothing between the two.
 *
 * `lg` between the two sections, the gap `.body` put between them before they were one block, and `lg`
 * under the line to match `.body`'s gap on its far side.
 */
.summary {
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
  padding-bottom: var(--space__lg);
  border-bottom: 1px solid var(--color__gray-300);
}

/*
 * The executive summary's and the buyer actions' bullets (PLT-7052), the chat card's own list shape.
 *
 * `sm` between bullets, a step in from the `md` between a section's label and its list: the bullets are
 * one group under that label, so they sit closer to each other than the label sits to them
 * (design/10-foundations.md § Space and rhythm). `lg` of indent is the marker's own room. The markers take the meta ink rather than the text's, so the
 * bullets' text is the darkest thing in the block.
 */
.summaryList {
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
  margin: 0;
  padding-left: var(--space__lg);
  list-style: disc outside;
  color: var(--color__gray-600);
}

/*
 * The summary's skeleton sections hold their children at their own width. `Skeleton` paints the
 * element it wraps, and in `.section`'s column a label `Text` is stretched to the full row, so its
 * bar would run the panel's width where the loaded label is one short phrase.
 */
.summarySkeleton {
  align-items: flex-start;
}

/*
 * A grid, not a flex row per line.
 *
 * The mark and its word are one right-aligned group, so with every row sizing itself the marks
 * landed on a different x in each line — "Low" and "Very high" are not the same width — and a column
 * of marks that starts somewhere new every row is precisely the column the mark exists to make
 * scannable. One track sized to the widest value cell puts every mark on one line. `.row` is
 * `display: contents` so each row's three cells become the grid's own items, which is the same trick
 * `TableV2`'s `Tr` uses to let a row's style reach its cells.
 *
 * No `column-gap`: each cell pads its own edge, which keeps every gap on the side of the cell that
 * owns it and lets the rail's gap live inside the rail's own track.
 *
 * **The value track keeps its floor** even though one list can no longer step between sections. It
 * still earns it: sized purely to its content the column moves whenever the widest word in the list
 * changes, so a supplier whose only gap row says "Not modelled" would put its marks 26px from where
 * the next supplier's sit. 142px is that cell at its widest *possible* reading — the 41px mark, the
 * `sm` gap beside it, the cell's own left padding, and the longest word any row can carry, which is
 * not a band word: the gap words are wider, and "Not modelled" measures 141.28px on the live page.
 * The MCP card fixes the same column for the same reason (`mcp-apps/supplier-risk/card.tsx`,
 * `.risk-row`).
 *
 * What the name track gets is the panel's width less its two `xl` insets, this floor and the 20px
 * gutter: 390px at the 600px ceiling, and 150px once the panel's `clamp()` bottoms out at 360px,
 * which it does on any window under about 860px. At that floor a couple of the longer names clip
 * ("Water and marine resources"), which is the trade: a truncated name still reads and its full text
 * is one hover away, while a mark column that moves cannot be read at all.
 */
.rows {
  display: grid;
  /*
   * Four tracks: the disclosure rail, the category gutter, the name, and the level.
   *
   * The gutter is a fixed 20px track rather than an auto one, so every letter sits on the same x
   * whatever the rows below it hold — the column is read as a column, which is the reason the
   * category is a gutter and not a word inside the name cell.
   *
   * **The chevron leads the row** (Roni, 2026-09-22, PLT-6826). Leading is where a disclosure is read
   * for: the reader meets "this opens" before they meet what it opens, the chevrons make a column of
   * their own, and the band words keep the right edge to themselves. 24px is a 16px glyph and the `sm`
   * gap to the letter.
   *
   * What the rail and the gutter cost the name track, together: 44px against a list with neither —
   * 390px becomes 366px at the panel's 600px ceiling for the rail alone, and 150px becomes 126px once
   * the `clamp()` bottoms out at 360px. At that floor a couple more names truncate than before the rail.
   * The alternative is no chevron, and then seventeen rows give a reader no reason to click any of them:
   * a hover well is a state treatment, and a state treatment cannot advertise an affordance at rest
   * (`design/50-components.md` § Focus and interaction states).
   */
  grid-template-columns: 24px 20px minmax(0, 1fr) minmax(142px, max-content);
}

/*
 * A row is one `<button>` spanning the grid, with `subgrid` so its cells still land on the list's
 * own columns (PLT-6826).
 *
 * The cells cannot be the grid's own items any more — the `display: contents` row they used to be —
 * because a row that opens has to be ONE control: a click handler on a `div` is not a button to a
 * keyboard, and a control per cell would put the target somewhere other than the thing it opens.
 * `subgrid` is what makes the row an element without giving up the column alignment the list is built
 * on. The skeleton's rows take the same class so they wait on the same tracks.
 */
.row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;
  border-bottom: 1px solid var(--color__gray-200);
}

/*
 * The button half of a row. A UA button carries an appearance, a font and inline padding of its own,
 * and none of it is wanted: `font: inherit` is what keeps every word on the surface coming from `Text`.
 *
 * No focus rule of its own — `reset.css` paints the platform ring on `button:focus-visible`, which is
 * exactly this element.
 */
button.row {
  appearance: none;
  width: 100%;
  margin: 0;
  padding: 0;
  border-top: none;
  border-right: none;
  border-left: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

/*
 * The hover well, which is what tells a reader the row is a control before they commit to it —
 * `gray-50` is the tint the system allows for exactly this (`design/10-foundations.md`: gray tints are
 * for hover, wells and alternate rows).
 *
 * It stops at the list's own edge rather than bleeding to the panel's inset: `.body` scrolls, and
 * `overflow-y: auto` computes `overflow-x` to `auto` too, so anything painted outside the content box
 * buys the whole panel a horizontal scrollbar.
 */
button.row:hover {
  background: var(--color__gray-50);
}

/* The row and the block it opened are one unit, so the seam goes under the pair rather than between
   them. Without this the take-away reads as belonging to the row below it. */
.row[aria-expanded='true'] {
  border-bottom: none;
}

/* The list's last element, row or open block, draws no seam: the list's own edge is the body's padding. */
.rows > :last-child {
  border-bottom: none;
}

.rowCategory,
.rowName,
.rowValue {
  padding: var(--space__sm) 0;
}

/*
 * The disclosure rail: a chevron pointing right when closed, down when open.
 *
 * The glyph sits at the start of the 24px track and the 8px it leaves is the gap to the letter — not
 * `padding` and not `justify-content: center`, either of which makes the cell's box wider than its
 * glyph, and the turn below rotates the glyph about its own centre only if the two are the same.
 *
 * `gray-600` is the meta ink: the chevron says a row can open and nothing else, so it must not compete
 * with the letter beside it, which is the one thing on the row whose colour carries meaning.
 */
.rowDisclosure {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  color: var(--color__gray-600);
}

/*
 * The turn is on the glyph, never on the cell. 150ms written as a literal: `tokens.css` carries no
 * duration token and `design/10-foundations.md` rules out minting one here. It is on the Carbon scale
 * the product's motion comes from, and a step under the panel's own 180ms entrance.
 */
.rowDisclosure svg {
  transition: transform 150ms ease-out;
}

.row[aria-expanded='true'] .rowDisclosure svg {
  transform: rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .rowDisclosure svg {
    transition: none;
  }
}

/*
 * What a row opens to (PLT-6826): the take-away, the conditions a hazard was read under, and one way on.
 *
 * **Indented past the disclosure rail and no further.** 24px is the rail's own track, so the block
 * starts on the row's content edge — where the category letter begins — which is how a disclosure
 * aligns: the rail is a rail, and everything it opens lines up after it. Indenting to the name would
 * cost another 20px of measure, and at the panel's 360px floor the line has 268px to wrap in as it is.
 *
 * `md` between the block's own parts rather than `sm`. At `sm` the qualifier read as a fourth line of
 * the take-away in a smaller type rather than as a separate claim about it — both are `gray-600` prose,
 * so the gap is the only thing separating them and it has to be a step the eye registers.
 */
.takeaway {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space__md);
  padding: 0 0 var(--space__md) 24px;
  border-bottom: 1px solid var(--color__gray-200);
}

/*
 * The way on, pulled left by `Button`'s own inline padding (12px, which is `md`) so its icon starts
 * where the take-away's first character does. Measured between what a reader can see rather than
 * between box edges (`design/10-foundations.md` § Optical spacing): left alone, the button's box lines
 * up and its sparkles icon sits 12px inside the paragraph above it.
 */
.takeawayAction {
  margin-left: calc(-1 * var(--space__md));
}

/*
 * The gutter cell. `sm` of padding on its right is the space between the letter and the name —
 * put here rather than as a `column-gap` on the grid, because a gap would break the row's
 * hairline into three pieces.
 */
.rowCategory {
  padding-right: var(--space__sm);
}

/*
 * The gutter letter, in its category's hue.
 *
 * **E, S and G take the documented ESG mapping, not a new one.** `design/10-foundations.md`
 * reserves `teal-700` for the Environment ESG category, `yellow-700` for "the Social ESG category
 * only" and `lilac-800` for Governance, and `get_style_guide` hands AI-generated artifacts those
 * same three with "swapping them or inventing variants breaks recognisability across reports".
 * Reusing them is what keeps a reader from meeting Social in one hue in a generated report and
 * another here.
 *
 * *Note the one place the product disagrees with its own document:* `dma-v2-view/dashboard/
 * utils.ts` paints Social `orange-800` and Environment `teal-800`. That is an undocumented
 * divergence rather than the house mapping, and this file deliberately follows the document and
 * the style guide instead — an earlier version of these rules copied `utils.ts` and claimed the
 * three sources agreed, which they do not.
 *
 * **Climate is gray, and deliberately not a fourth hue.** It is the one row type that is not an
 * ESRS category — a physical-grid reading rather than a materiality finding — and
 * `design/10-foundations.md` is explicit that a label carrying none of the reserved meanings is
 * gray. So the set needs no new reserved colour role and no edit to the design document, which
 * every non-gray candidate would have required. It also says something true: the gray letter marks
 * the rows that are a different kind of reading from the other fourteen.
 *
 * *Measured, not eyeballed.* Pairwise CIEDE2000 over the four: 16.0 at its worst (gray-700 against
 * lilac-800), 26+ for every other pair, and all four clear 4.5:1 on white (10.3 / 5.5 / 4.9 / 9.9).
 * That worst pair is the one to be honest about — it is below the ~20 a glyph normally wants — but
 * the two differ by 69 units of chroma (12 against 81), which is the channel the eye actually
 * separates here and the one CIEDE2000 compresses hardest. An earlier attempt at four hues of its
 * own paired climate purple-900 with governance lilac-800 at dE 4.9, which a reader saw as one
 * colour under two names (Roni, 2026-09-17), and its replacement gave climate `blue-700`, which
 * the document reserves for the selected segment of a pill tab switcher and nothing else.
 *
 * **Ink, not a filled pill.** `design/50-components.md` § Tables warns that a tint repeated down a
 * column stops being a badge and becomes a band, and seventeen filled cells in a 360px panel is
 * that case. A coloured glyph tells the categories apart without painting a block.
 *
 * **Nothing here is purple**, which is load-bearing rather than incidental: purple is the
 * negative-impact hue this panel's own level marks are painted in, and a purple letter beside them
 * would read as part of the severity encoding rather than as a category.
 *
 * The default is `gray-600`, the meta ink this panel already uses, so a category this file has no
 * letter for never borrows another category's colour. Not `gray-500`, which the document rules out
 * for anything carrying meaning: "a label never goes below the contrast floor to look secondary".
 */
/* No `cursor` of its own: the letter is part of the row's button now, and the button's pointer is
   the true one for it. */
.categoryMark {
  color: var(--color__gray-600);
}

.categoryMark[data-category='climate'] {
  color: var(--color__gray-700);
}

.categoryMark[data-category='environment'] {
  color: var(--color__teal-700);
}

.categoryMark[data-category='social'] {
  color: var(--color__yellow-700);
}

.categoryMark[data-category='governance'] {
  color: var(--color__lilac-800);
}

/* Takes the space the level column does not, so a long topic name truncates rather than pushing the
   mark out of the panel. */
.rowName {
  min-width: 0;
}

/* The mark leads and the word follows, and the pair never wraps. */
.rowValue {
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  padding-left: var(--space__sm);
  white-space: nowrap;
}

/*
 * Under the rows and centred across them (Roni, 2026-09-15). It sat at the rows' left edge
 * before, which is where an inline control belonging to the block above it would normally go;
 * centred, it reads as the end of the list rather than as one more item in the name column, and
 * the control sits under the middle of the list however wide the names beside it are.
 *
 * A `justify-content` on the wrapper rather than a `margin: auto` on the button, so the button's
 * own hit area still ends where its label does.
 */
.expand {
  display: flex;
  justify-content: center;
}
